// User-defined attributes: how a PROMOTED attribute reads in a line grid
// (prototype screen 2): a faint "n/a" where the line does not have it, a red
// "Missing" where it is required and empty, otherwise the value's label with
// its colour swatch. One component for every grid that shows them.

import type { ReactNode } from 'react'
import type { ColumnDef } from '../txn/types'
import { ddlbDisplayLabel } from '../txn/utils'
import { cellApplies, cellMissing, swatchOf } from './merge'

export function UdaNaLabel() {
  return (
    <span style={{ color: 'var(--color-text-faint, var(--color-text-secondary))', fontStyle: 'italic', fontSize: 13 }}>
      n/a
    </span>
  )
}

export function UdaMissingLabel() {
  return <span style={{ color: 'var(--color-error)', fontSize: 13, fontWeight: 500 }}>Missing</span>
}

export function UdaSwatch({ color }: { color: string }) {
  return (
    <span aria-hidden style={{
      width: 14, height: 14, borderRadius: '50%', flexShrink: 0, display: 'inline-block',
      background: color, border: '1px solid var(--color-border-strong)',
    }} />
  )
}

/** A promoted attribute's display cell. `fallback` renders an ordinary value
 *  (the grid's own formatter) when the column is not an attribute. */
export function UdaValueCell({ col, value, attrKeys, fallback }: {
  col: ColumnDef
  value: unknown
  /** The row's applicable attributes (null while not known). */
  attrKeys: readonly string[] | null | undefined
  fallback?: ReactNode
}) {
  if (!col.uda || col.uda.driver) return <>{fallback}</>
  if (!cellApplies(col, attrKeys)) return <UdaNaLabel />
  if (cellMissing(col, value, attrKeys)) return <UdaMissingLabel />
  if (value === null || value === undefined || value === '') return <>{fallback}</>
  const swatch = swatchOf(col, value)
  const label = ddlbDisplayLabel(value, col) || String(value)
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
      {swatch && <UdaSwatch color={swatch} />}
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
    </span>
  )
}
